import { useMutation } from '@apollo/client'
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ExternalLink,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input
} from '@chain-zen/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { v4 as uuidv4 } from 'uuid'
import * as z from 'zod'

import { UPDATE_FOUR_KITES_APP } from '../../../data/mutations/apps'
import { GET_APPS } from '../../../data/queries/apps'
import { useAuth } from '../../../hooks/use-auth'
import type { AppTypeEnum } from '../../../types/workspace'
import type { AppModalProps } from '../apps-data'

type TCustomer = {
  customerID: string
  customerIDInChaine: string
  customerNameInChaine: string
  integrationConfigurationID?: string
}

const formSchema = z.object({
  apiKey: z.string().min(1, 'API Key is required'),
  customerIds: z
    .array(
      z.object({
        customerID: z.string().min(1, 'Customer ID is required'),
        customerIDInChaine: z.string().min(1, 'Chain Customer ID is required'),
        customerNameInChaine: z.string().min(1, 'Chain Customer Name is required'),
        integrationConfigurationID: z.string().optional()
      })
    )
    .min(1, 'At least one customer ID is required')
})

type UpdateFourKitesAppInput = {
  apiKey: string
  appType: AppTypeEnum
  updates: { created: TCustomer[]; deleted: TCustomer[] }
}

/**
 * This is the modal for the FourKitesCarrierUpdateModal app.
 */
export function FourKitesCarrierUpdateModal({ isOpen, onClose, appType, app }: AppModalProps) {
  const { selectedWorkspace } = useAuth()
  const workspaceID = selectedWorkspace?.workspace.id || ''
  const [formError, setFormError] = useState('')
  const existingCustomers = app?.workspaceApp?.appMetadata?.customers || []
  const existingApiKey = app?.workspaceApp?.appCredentials?.apiKey || ''

  const deletedCustomersRef = useRef<TCustomer[]>([])
  const createdCustomersRef = useRef<TCustomer[]>([])

  // Transform existing customers to ensure customerNameInChaine is always a string
  const initialCustomers =
    existingCustomers.length > 0
      ? existingCustomers.map((customer) => ({
          customerID: customer.customerID,
          customerIDInChaine: customer.customerIDInChaine || '',
          // Ensure customerNameInChaine is always a string
          customerNameInChaine: customer.customerNameInChaine || '',
          integrationConfigurationID: customer.integrationConfigurationID
        }))
      : [
          {
            customerID: '',
            customerIDInChaine: '',
            customerNameInChaine: ''
          }
        ]

  const form = useForm<z.infer<typeof formSchema>>({
    defaultValues: {
      apiKey: existingApiKey,
      customerIds: initialCustomers
    },
    resolver: zodResolver(formSchema)
  })

  const [updateFourKitesApp, { loading }] = useMutation<
    {
      updateCustomersInFourKitesCarrier: {
        apiKey: string
        appType: AppTypeEnum.FourKitesCarrier
        updates: { created: TCustomer[]; deleted: TCustomer[] }
      }
    },
    UpdateFourKitesAppInput
  >(UPDATE_FOUR_KITES_APP, {
    context: {
      headers: {
        'ws-id': workspaceID
      }
    },
    onCompleted: () => {
      toast.success('FourKites app is updated successfully!')
      handleClose()
    },
    onError: (err) => {
      const errorMessage =
        err.message === 'Incorrect api key'
          ? 'Invalid API Key. Please try again.'
          : `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`
      setFormError(errorMessage)
      toast.error(errorMessage)
    },
    refetchQueries: [GET_APPS]
  })

  const handleClose = () => {
    setFormError('')
    form.reset()
    deletedCustomersRef.current = []
    createdCustomersRef.current = []
    onClose()
  }

  const addCustomerId = () => {
    const currentCustomerIds = form.getValues('customerIds')
    const newCustomer = {
      customerID: '',
      customerIDInChaine: '',
      customerNameInChaine: '',
      integrationConfigurationID: uuidv4()
    }
    form.setValue('customerIds', [...currentCustomerIds, newCustomer])
    createdCustomersRef.current.push(newCustomer)
  }

  const removeCustomerId = (index: number) => {
    const currentCustomerIds = form.getValues('customerIds')
    if (currentCustomerIds.length > 1) {
      const customerToRemove = currentCustomerIds[index]
      if (customerToRemove.integrationConfigurationID) {
        deletedCustomersRef.current.push(customerToRemove as TCustomer)
      }
      form.setValue(
        'customerIds',
        currentCustomerIds.filter((_, i) => i !== index)
      )
    }
  }

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    const newCustomers = values.customerIds.filter(
      (customer) =>
        !customer.integrationConfigurationID ||
        createdCustomersRef.current.some((c) => c.integrationConfigurationID === customer.integrationConfigurationID)
    )

    updateFourKitesApp({
      variables: {
        apiKey: values.apiKey,
        appType: appType,
        updates: {
          created: newCustomers,
          deleted: deletedCustomersRef.current
        }
      }
    })
  }

  const isExistingCustomer = (customer: TCustomer) => {
    return existingCustomers.some((c) => c.customerID === customer.customerID)
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg w-full max-h-[70vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Update FourKites for Chain</DialogTitle>
          <DialogDescription>Update your FourKites integration settings and customer IDs.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <div className="space-y-6">
            <FormField
              control={form.control}
              name="apiKey"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>API Key</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your API Key" {...field} disabled={loading} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {form.watch('customerIds').map((customer, index) => (
              <div
                key={customer.integrationConfigurationID || index}
                className="space-y-4 rounded-lg bg-gray-50 p-4 dark:bg-gray-900"
              >
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium">Customer {index + 1}</h4>
                  {form.watch('customerIds').length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeCustomerId(index)}
                      disabled={loading}
                    >
                      Remove
                    </Button>
                  )}
                </div>

                <FormField
                  control={form.control}
                  name={`customerIds.${index}.customerID`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>FourKites Customer ID</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter FourKites Customer ID"
                          {...field}
                          disabled={isExistingCustomer(customer) || loading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`customerIds.${index}.customerIDInChaine`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Chain Customer ID</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter Chain Customer ID"
                          {...field}
                          disabled={isExistingCustomer(customer) || loading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`customerIds.${index}.customerNameInChaine`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Chain Customer Name</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter Chain Customer Name"
                          {...field}
                          disabled={isExistingCustomer(customer) || loading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            ))}

            <Button type="button" variant="outline" onClick={addCustomerId} disabled={loading}>
              Add Another Customer
            </Button>

            {!!formError && <Alert variant="error" description={formError} />}

            <DialogFooter className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:gap-0">
              <a
                href="https://handbook.chaineapp.com/product-guide/apps/four-kites"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-sm text-blue-600 hover:underline"
              >
                See detailed steps here
                <ExternalLink className="h-4 w-4" />
              </a>

              <div className="space-x-2">
                <Button variant="outline" onClick={handleClose} type="button">
                  Cancel
                </Button>
                <Button type="submit" disabled={loading} onClick={() => form.handleSubmit(onSubmit)}>
                  {loading ? 'Updating...' : 'Update'}
                </Button>
              </div>
            </DialogFooter>
          </div>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
